import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../../services/api";
import { format, parseISO } from "date-fns";
import { useToast } from "../../context/ToastContext";

const reqStatusBadge = (status) => {
  const map = {
    "Pending":          "ims-badge-pending",
    "Approved":         "ims-badge-approved",
    "Rejected":         "ims-badge-rejected",
    "Issued":           "ims-badge-full",
    "Partially Issued": "ims-badge-partial",
  };
  return <span className={`ims-badge ${map[status] || "ims-badge-pending"}`}>{status}</span>;
};

const ProcessRequisition = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [requisition, setRequisition]   = useState(null);
  const [approvedItems, setApprovedItems] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const user     = JSON.parse(localStorage.getItem("user"));
  const userRole = user?.role || "";

  useEffect(() => {
    const fetchRequisition = async () => {
      try {
        const res = await axios.get(`/requisitions/${id}`);
        const data = res.data;
        setRequisition(data.requisition);
        setApprovedItems(data.items.map((item) => ({
          item_id:            item.item_id,
          item_name:          item.item_name,
          available_stock:    parseInt(item.available_stock),
          requested_quantity: parseInt(item.requested_quantity),
          issued_quantity:    parseInt(item.issued_quantity) || 0,
          approved_quantity:  item.approved_quantity > 0
            ? parseInt(item.approved_quantity)
            : parseInt(item.requested_quantity),
        })));
        setLoading(false);
      } catch (err) {
        console.error("Error fetching requisition:", err);
        setLoading(false);
      }
    };
    fetchRequisition();
  }, [id]);

  const handleQuantityChange = (itemId, value) => {
    if (value < 0) return;
    const item = approvedItems.find((i) => i.item_id === itemId);
    if (!item) return;
    if (value > item.requested_quantity) {
      showToast("warning", "Invalid Quantity", "Approved quantity cannot exceed requested quantity.");
      return;
    }
    if (requisition.status === "Partially Issued" && value < item.issued_quantity) {
      showToast("warning", "Invalid Quantity", "Approved quantity cannot be less than already issued quantity.");
      return;
    }
    setApprovedItems((prev) =>
      prev.map((i) => i.item_id === itemId ? { ...i, approved_quantity: value } : i)
    );
  };

  const handleProcess = async (status) => {
    if (["Rejected", "Issued"].includes(requisition.status)) {
      showToast("warning", "Not Allowed", "Requisitions in Rejected or Issued status cannot be modified.");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.put(`/requisitions/process/${id}`, {
        status,
        approvedItems: status === "Approved" ? approvedItems : [],
      });
      showToast("success", `Requisition ${status}`, `Requisition #${id} has been ${status.toLowerCase()} successfully.`);
      navigate("/dashboard/requisition-list");
    } catch (err) {
      console.error("Error processing requisition:", err);
      showToast("error", "Failed", "Could not process requisition. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <i className="fas fa-spinner fa-spin fa-2x text-primary"></i>
      </div>
    );
  }

  if (!requisition) {
    return <div className="alert alert-danger">Requisition not found.</div>;
  }

  const isIssued   = requisition.status === "Issued";
  const isRejected = requisition.status === "Rejected";
  const isLocked   = isIssued || isRejected;

  return (
    <div>
      {/* ── Page header ── */}
      <div className="ims-page-header">
        <div>
          <h4>Process Requisition — #{id}</h4>
          <p>Review and approve or reject the items below</p>
        </div>
        <button className="btn btn-sm btn-secondary" onClick={() => navigate("/dashboard/requisition-list")}>
          <i className="fas fa-arrow-left mr-1"></i> Back
        </button>
      </div>

      {/* ── Details card ── */}
      <div className="card shadow-sm mb-4" style={{ border: "none", borderRadius: "12px" }}>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <table style={{ fontSize: "13px", width: "100%" }}>
                <tbody>
                  <tr>
                    <td style={{ color: "#64748b", width: "130px", paddingBottom: "8px" }}>Requested By</td>
                    <td><strong>{requisition.user_name}</strong></td>
                  </tr>
                  <tr>
                    <td style={{ color: "#64748b", paddingBottom: "8px" }}>Created Date</td>
                    <td>{format(parseISO(requisition.created_date), "dd/MM/yyyy")}</td>
                  </tr>
                  <tr>
                    <td style={{ color: "#64748b", paddingBottom: "8px" }}>Phone</td>
                    <td>{requisition.user_phone || "—"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="col-md-6">
              <table style={{ fontSize: "13px", width: "100%" }}>
                <tbody>
                  <tr>
                    <td style={{ color: "#64748b", width: "130px", paddingBottom: "8px" }}>Status</td>
                    <td>{reqStatusBadge(requisition.status)}</td>
                  </tr>
                  <tr>
                    <td style={{ color: "#64748b", paddingBottom: "8px" }}>Priority</td>
                    <td>
                      <span className={`ims-badge ${requisition.priority === "High" ? "ims-badge-rejected" : "ims-badge-principal"}`}>
                        {requisition.priority}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: "#64748b", paddingBottom: "8px" }}>Expected Date</td>
                    <td>{new Date(requisition.expected_date).toLocaleDateString("en-GB")}</td>
                  </tr>
                  {requisition.req_description && (
                    <tr>
                      <td style={{ color: "#64748b", paddingBottom: "8px" }}>Purpose</td>
                      <td>{requisition.req_description}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ── Items table ── */}
      <div className="card shadow-sm mb-4" style={{ border: "none", borderRadius: "12px" }}>
        <div className="card-header" style={{ background: "#f8fafc", borderRadius: "12px 12px 0 0", borderBottom: "1px solid #f1f5f9" }}>
          <strong style={{ fontSize: "14px" }}>
            <i className="fas fa-boxes mr-2 text-primary"></i>Items
          </strong>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="ims-table">
              <thead>
                <tr>
                  <th>Item Name</th>
                  <th>Available Stock</th>
                  <th>Requested Qty</th>
                  {userRole !== "Principal" && <th>Issued Qty</th>}
                  <th>Approved Qty</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {approvedItems.map((item) => (
                  <tr key={item.item_id}>
                    <td><strong>{item.item_name}</strong></td>
                    <td>{item.available_stock}</td>
                    <td>{item.requested_quantity}</td>
                    {userRole !== "Principal" && <td>{item.issued_quantity}</td>}
                    <td style={{ width: "130px" }}>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={item.approved_quantity}
                        min="0"
                        max={item.requested_quantity}
                        onChange={(e) => handleQuantityChange(item.item_id, parseInt(e.target.value) || 0)}
                        disabled={isLocked}
                        style={{ width: "90px" }}
                      />
                    </td>
                    <td>{reqStatusBadge(requisition.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Action buttons ── */}
      {!isLocked && (
        <div className="d-flex gap-2">
          <button
            className="btn btn-success btn-sm"
            onClick={() => handleProcess("Approved")}
            disabled={isSubmitting}
          >
            {isSubmitting ? <i className="fas fa-spinner fa-spin mr-1"></i> : <i className="fas fa-check mr-1"></i>}
            Approve
          </button>
          <button
            className="btn btn-danger btn-sm"
            onClick={() => handleProcess("Rejected")}
            disabled={isSubmitting}
          >
            <i className="fas fa-times mr-1"></i> Reject
          </button>
        </div>
      )}

      {isLocked && (
        <div className="alert alert-secondary" style={{ fontSize: "13px", borderRadius: "8px" }}>
          <i className="fas fa-lock mr-2"></i>
          This requisition is <strong>{requisition.status}</strong> — no further action required.
        </div>
      )}
    </div>
  );
};

export default ProcessRequisition;
